import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Subscriber {
  id: string;
  email: string;
  is_active: boolean;
  subscribed_at: string;
  unsubscribed_at?: string;
}

export const useNewsletterSubscribers = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .order('subscribed_at', { ascending: false });

      if (error) throw error;

      setSubscribers(data || []);
    } catch (error) {
      console.error('Error fetching newsletter subscribers:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել բաժանորդներին",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sendNewsletterEmail = async (subject: string, content: string, subscriberEmails?: string[]) => {
    try {
      // Call the newsletter sending edge function
      const { data, error } = await supabase.functions.invoke('send-newsletter', {
        body: {
          subject,
          content,
          recipientEmails: subscriberEmails
        }
      });

      if (error) {
        console.error('Error calling send-newsletter function:', error);
        throw error;
      }

      if (data?.success) {
        toast({
          title: "Հաջողություն",
          description: `Newsletter-ը ուղարկվեց ${data.recipients} բաժանորդի`,
        });
        return true;
      } else {
        throw new Error(data?.error || 'Failed to send newsletter');
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ուղարկել newsletter-ը",
        variant: "destructive"
      });
      return false;
    }
  };

  const toggleSubscriberStatus = async (subscriberId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .update({ 
          is_active: isActive,
          unsubscribed_at: isActive ? null : new Date().toISOString()
        })
        .eq('id', subscriberId);

      if (error) throw error;

      // Update local state
      setSubscribers(prev =>
        prev.map(sub =>
          sub.id === subscriberId 
            ? { ...sub, is_active: isActive, unsubscribed_at: isActive ? undefined : new Date().toISOString() }
            : sub
        )
      );

      toast({
        title: "Հաջողություն",
        description: `Բաժանորդի կարգավիճակը ${isActive ? 'ակտիվացվեց' : 'անջատվեց'}`,
      });

      return true;
    } catch (error) {
      console.error('Error updating subscriber status:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց փոխել բաժանորդի կարգավիճակը",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  return {
    subscribers,
    loading,
    fetchSubscribers,
    sendNewsletterEmail,
    toggleSubscriberStatus
  };
};